import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  CRow,
  CCol,
  CCard,
  CCardHeader,
  CCardBody,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CBadge,
  CButton,
  CTooltip,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { 
  cilMediaPlay, 
  cilCheckCircle, 
  cilMap, 
  cilChevronLeft, 
  cilChevronRight, 
  cilX, 
  cilZoomIn, 
  cilZoomOut,
  cilInfo,
  cilWarning,
  cilPencil,
  cilTrash,
  cilFile,
  cilChartLine,
  cilChevronBottom,
  cilChevronTop
} from "@coreui/icons";
import dayjs from "dayjs";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { ResponsivePie } from "@nivo/pie";
import axios from "axios";
import { toast } from "react-toastify";

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return "00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}ч ${m}м`;
  return `${m}м ${s}с`;
};

const formatDurationVerbose = (seconds) => {
  if (!seconds || seconds <= 0) return "0 мин 00 сек";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m} мин ${String(s).padStart(2, "0")} сек`;
};

// =========================================================================
//        КООРДИНАТЫ ПОЛИГОНА ТКО ДЛЯ ИСКЛЮЧЕНИЯ ИЗ АКТА КГО
// =========================================================================
const LANDFILL_LAT = 54.55399;
const LANDFILL_LON = 39.77575;
const LANDFILL_RADIUS_METERS = 500;

const isNearLandfill = (lat, lon) => {
  const numLat = Number(lat);
  const numLon = Number(lon);
  if (!numLat || !numLon) return false;

  const R = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(numLat - LANDFILL_LAT);
  const dLon = toRad(numLon - LANDFILL_LON);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(LANDFILL_LAT)) * Math.cos(toRad(numLat)) * Math.sin(dLon / 2) ** 2;
  const distanceMeters = 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return distanceMeters <= LANDFILL_RADIUS_METERS;
};

// =========================================================================
//        ФУНКЦИЯ ПЕЧАТИ / PDF «ВЫЕЗД ЗА КГО» ПО СТАНДАРТУ ГОСТ
// =========================================================================
const printKgoActHtml = ({ sites, dateRange, deviceName, orgName }) => {
  const kgoSites = sites.filter(
    (s) => s.has_bulky_waste && !isNearLandfill(s.latitude, s.longitude)
  );
  
  const docDate = dayjs().format("DD.MM.YYYY");
  const periodStr = `${dayjs(dateRange?.start).format("DD.MM.YYYY")} — ${dayjs(dateRange?.end).format("DD.MM.YYYY")}`;
  
  let totalVol = 0;
  kgoSites.forEach((s) => {
    const v = parseFloat(s.llm_analysis?.estimated_volume_m3) || 1.5;
    totalVol += v;
  });

  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Разрешите всплывающие окна в браузере для печати акта");
    return;
  }

  const rowsHtml = kgoSites.map((s, idx) => {
    const addr = s.address || `КП (${s.latitude?.toFixed(5)}, ${s.longitude?.toFixed(5)})`;
    const coords = (s.latitude > 0 && s.longitude > 0) ? `${s.latitude.toFixed(5)}, ${s.longitude.toFixed(5)}` : "—";
    const vol = s.llm_analysis?.estimated_volume_m3 ? `~${s.llm_analysis.estimated_volume_m3} м³` : "~1.5 м³";
    const items = s.llm_analysis?.kgo_items?.length ? s.llm_analysis.kgo_items.join(", ") : "Крупногабаритная мебель, ветки, доски";

    const photoBefore = s.photo_before_url 
      ? `<img src="${s.photo_before_url}" style="width: 100px; height: 70px; object-fit: cover; border: 1px solid #999;" />` 
      : `<span style="color:#888;">Нет фото</span>`;
    const photoAfter = s.photo_after_url 
      ? `<img src="${s.photo_after_url}" style="width: 100px; height: 70px; object-fit: cover; border: 1px solid #28a745;" />` 
      : `<span style="color:#888;">Нет фото</span>`;

    return `
      <tr>
        <td style="text-align: center; font-weight: bold;">${idx + 1}</td>
        <td>
          <b>${addr}</b><br/>
          <span style="font-size: 8.5pt; color: #555;">Координаты: ${coords}</span>
        </td>
        <td style="text-align: center;">
          ${dayjs(s.start).format("DD.MM.YYYY")}<br/>
          ${dayjs(s.start).format("HH:mm:ss")} — ${dayjs(s.end).format("HH:mm:ss")}
        </td>
        <td>
          <b style="color: #b30000;">${vol}</b><br/>
          <span style="font-size: 8.5pt;">${items}</span>
        </td>
        <td style="text-align: center;">
          <div style="display: flex; gap: 6px; justify-content: center; align-items: center;">
            <div style="text-align: center;"><span style="font-size: 7.5pt; display: block;">ДО:</span>${photoBefore}</div>
            <div style="text-align: center;"><span style="font-size: 7.5pt; display: block;">ПОСЛЕ:</span>${photoAfter}</div>
          </div>
        </td>
      </tr>
    `;
  }).join("");

  const fullHtml = `
    <!DOCTYPE html>
    <html lang="ru">
    <head>
      <meta charset="utf-8">
      <title>Акт-предписание на вывоз КГО — ${docDate}</title>
      <style>
        @page {
          size: A4 portrait;
          margin: 15mm 10mm 15mm 15mm;
        }
        body {
          font-family: "Times New Roman", Times, serif;
          font-size: 10pt;
          line-height: 1.25;
          color: #000;
          background: #fff;
          margin: 0;
          padding: 10px;
        }
        .header {
          text-align: center;
          margin-bottom: 15px;
          border-bottom: 2px solid #000;
          padding-bottom: 8px;
        }
        .org-name {
          font-size: 11pt;
          font-weight: bold;
          text-transform: uppercase;
        }
        .doc-title {
          font-size: 13pt;
          font-weight: bold;
          margin-top: 5px;
          text-transform: uppercase;
        }
        .doc-meta {
          font-size: 9pt;
          margin-top: 6px;
          color: #222;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
        }
        th, td {
          border: 1px solid #000;
          padding: 5px 6px;
          vertical-align: middle;
          font-size: 9pt;
        }
        th {
          background-color: #f2f2f2;
          font-weight: bold;
          text-align: center;
        }
        tr {
          page-break-inside: avoid;
        }
        .summary-box {
          margin-top: 15px;
          font-size: 9.5pt;
          line-height: 1.4;
          page-break-inside: avoid;
        }
        .sign-table {
          width: 100%;
          margin-top: 25px;
          border: none;
          page-break-inside: avoid;
        }
        .sign-table td {
          border: none;
          padding: 10px 0;
          font-size: 9.5pt;
        }
        @media print {
          .no-print { display: none !important; }
        }
      </style>
    </head>
    <body>
      <div class="no-print" style="background: #fff3cd; padding: 10px; border: 1px solid #ffeeba; margin-bottom: 15px; text-align: center;">
        <button onclick="window.print()" style="font-size: 13pt; font-weight: bold; padding: 6px 20px; cursor: pointer; background: #dc3545; color: white; border: none; border-radius: 4px;">
          🖨️ Распечатать / Сохранить в PDF
        </button>
        <div style="font-size: 9pt; color: #856404; margin-top: 5px;">В окне печати выберите «Сохранить как PDF» или отправьте на принтер.</div>
      </div>

      <div class="header">
        <div class="org-name">${orgName || "РЕГИОНАЛЬНЫЙ ОПЕРАТОР ПО ОБРАЩЕНИЮ С ТКО"}</div>
        <div class="doc-title">АКТ-ПРЕДПИСАНИЕ НА ВЫВОЗ КРУПНОГАБАРИТНЫХ ОТХОДОВ (КГО)</div>
        <div class="doc-meta">
          <b>Дата составления:</b> ${docDate} г. &nbsp;|&nbsp; 
          <b>Период контроля:</b> ${periodStr} &nbsp;|&nbsp; 
          <b>Мусоровоз:</b> ${deviceName || "Спецтехника"}
        </div>
      </div>

      ${kgoSites.length === 0 ? `
        <div style="text-align: center; padding: 40px; font-size: 11pt;">
          За выбранный период фактов наличия крупногабаритных отходов (КГО) на площадках не зафиксировано.
        </div>
      ` : `
        <table>
          <thead>
            <tr>
              <th style="width: 25px;">№</th>
              <th style="width: 200px;">Адрес площадки и координаты</th>
              <th style="width: 105px;">Время заезда</th>
              <th style="width: 140px;">Объем и характер КГО</th>
              <th style="width: 230px;">Фотофиксация нарушения (ДО / ПОСЛЕ)</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>

        <div class="summary-box">
          <b>ИТОГО ПО РЕЗУЛЬТАТАМ ОБСЛЕДОВАНИЯ:</b><br/>
          Выявлено контейнерных площадок с навалами КГО: <b>${kgoSites.length} шт.</b><br/>
          Суммарный ориентировочный объём к вывозу: <b>~${totalVol.toFixed(1)} м³</b>.<br/>
          Резолюция: <i>Требуется выделение наряда спецтранспорта (ломовоз / бункеровоз с гидроманипулятором).</i>
        </div>

        <table class="sign-table">
          <tr>
            <td style="width: 50%;">
              <b>Диспетчер / Аудитор ИИ:</b><br/><br/>
              ____________________ / ____________________
            </td>
            <td style="width: 50%;">
              <b>Водитель спецтранспорта КГО:</b><br/><br/>
              ____________________ / ____________________
            </td>
          </tr>
        </table>
      `}

      <script>
        window.onload = function() {
          setTimeout(function() { window.print(); }, 400);
        };
      </script>
    </body>
    </html>
  `;

  printWindow.document.open();
  printWindow.document.write(fullHtml);
  printWindow.document.close();
};

const GarbageTruckDashboard = ({
  data,
  onVideoJump,
  timelineComponent,
}) => {
  const kpis = data?.kpis || {};
  const sites = data?.sites_summary || [];

  // Флаг раскрытия расширенных графиков
  const [showExtendedCharts, setShowExtendedCharts] = useState(true);
  // Ревизия для мгновенного пересчета графиков после редактирования/удаления КП
  const [dataRevision, setDataRevision] = useState(0);

  // ================= СТЕЙТЫ МОДАЛОК РЕДАКТИРОВАНИЯ И УДАЛЕНИЯ =================
  const [selectedLlmSite, setSelectedLlmSite] = useState(null);

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [deletingSession, setDeletingSession] = useState(false);

  const handleOpenEdit = (site) => {
    setEditingSession({
      id: site.id,
      address: site.address || "",
      containers_count: site.containers_count || 1,
      has_overflow: site.has_overflow || false,
      has_bulky_waste: site.has_bulky_waste || false,
      has_container_defect: site.has_container_defect || false,
      has_unemptied_containers: site.has_unemptied_containers || false,
      estimated_volume_m3: site.llm_analysis?.estimated_volume_m3 || "",
      photo_after_url: site.photo_after_url || null,
      photo_before_url: site.photo_before_url || null,
      start: site.start,
      end: site.end,
      previewMode: "after",
    });
    setEditModalVisible(true);
  };

  const handleSaveSession = async () => {
    if (!editingSession) return;
    setSavingEdit(true);
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`/api/garbage-sessions/${editingSession.id}/`, editingSession, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Данные заезда обновлены!");
      setEditModalVisible(false);

      const target = sites.find((s) => s.id === editingSession.id);
      if (target) {
        Object.assign(target, editingSession);
        target.containers_count = Number(editingSession.containers_count) || 0;
        target.unemptied_bins_count = editingSession.has_unemptied_containers ? 1 : 0;
        target.emptied_containers_count = Math.max(0, target.containers_count - target.unemptied_bins_count);
        if (editingSession.estimated_volume_m3) {
          target.llm_analysis = target.llm_analysis || {};
          target.llm_analysis.estimated_volume_m3 = editingSession.estimated_volume_m3;
        }
        setDataRevision((r) => r + 1);
      }
    } catch (err) {
      toast.error("Ошибка сохранения данных");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;
    setDeletingSession(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/garbage-sessions/${sessionToDelete.id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Заезд удалён из журнала и таймлайна");
      setDeleteModalVisible(false);

      const idx = sites.findIndex((s) => s.id === sessionToDelete.id);
      if (idx !== -1) {
        sites.splice(idx, 1);
        setDataRevision((r) => r + 1);
      }
    } catch (err) {
      toast.error("Ошибка при удалении");
    } finally {
      setDeletingSession(false);
    }
  };

  // ================= НАСТРОЙКИ И ДАННЫЕ ГРАФИКА ЭФФЕКТИВНОСТИ (УКВ) =================
  const [ukvSettings, setUkvSettings] = useState({
    showBars: true,
    showLine: true,
    ukvMin: "",
    ukvMax: "",
    sessionMin: "",
    sessionMax: "",
  });

  const ukvScrollRef = useRef(null);
  const highlightTimeoutRef = useRef(null);
  const [highlightedUkvIndex, setHighlightedUkvIndex] = useState(null);

  const ukvData = useMemo(() => {
    const raw = data?.ukv_data;
    if (!raw || !raw.data || raw.data.length === 0) return null;

    const autoAvgUkv = raw.auto_avg_ukv || 22.0;
    const autoAvgSession = raw.auto_avg_session || 240.0;

    const bounds = {
      uMin: ukvSettings.ukvMin ? parseFloat(ukvSettings.ukvMin) : 18,
      uMax: ukvSettings.ukvMax ? parseFloat(ukvSettings.ukvMax) : 25,
      sMin: ukvSettings.sessionMin ? parseFloat(ukvSettings.sessionMin) : Math.round(autoAvgSession * 0.5),
      sMax: ukvSettings.sessionMax ? parseFloat(ukvSettings.sessionMax) : 240,
    };

    const processedData = raw.data.map((d) => {
      const reasons = [];
      if (d.ukv < bounds.uMin) reasons.push(`Быстрый захват бака (<${bounds.uMin.toFixed(0)}с)`);
      if (d.ukv > bounds.uMax) reasons.push(`Долгий захват бака (>${bounds.uMax.toFixed(0)}с)`);
      if (d.durationSec < bounds.sMin) reasons.push(`Быстрое обслуж. КП (<${bounds.sMin.toFixed(0)}с)`);
      if (d.durationSec > bounds.sMax) reasons.push(`Долгое обслуж. КП (>${bounds.sMax.toFixed(0)}с)`);

      return {
        ...d,
        isAnomaly: reasons.length > 0,
        anomalyReasons: reasons,
      };
    });

    return {
      data: processedData,
      bounds,
      autoAvgUkv,
      autoAvgSession,
      anomalies: processedData.filter((d) => d.isAnomaly),
      isLongPeriod: raw.is_long_period,
    };
  }, [data, ukvSettings, dataRevision]);

  // =========================================================================
  //        РАСЧЕТ ТАБЛИЦЫ «СВОДНЫЕ KPI ЭКИПАЖА ЗА СМЕНУ» (Задача #423)
  // =========================================================================
  const crewKpis = useMemo(() => {
    if (!sites || sites.length === 0) return null;

    const totalSites = sites.length;

    let totalBinsFound = 0;
    let totalBinsEmptied = 0;
    const unemptiedSitesList = [];

    sites.forEach((s, idx) => {
      const count = Number(s.containers_count) || 0;
      const unemptied = s.has_unemptied_containers
        ? (Number(s.unemptied_bins_count) || 1)
        : 0;
      const emptied = Math.max(0, count - unemptied);

      totalBinsFound += count;
      totalBinsEmptied += emptied;

      if (s.has_unemptied_containers) {
        unemptiedSitesList.push({
          siteNumber: idx + 1,
          site: s,
          unemptiedCount: unemptied,
        });
      }
    });

    const totalUnemptiedBins = totalBinsFound - totalBinsEmptied;

    const normSiteSec = ukvSettings.sessionMax ? parseFloat(ukvSettings.sessionMax) : 240;
    const totalDurationSec = sites.reduce((acc, s) => acc + (Number(s.duration_sec) || 0), 0);
    const avgSiteSec = totalDurationSec / totalSites;
    const diffSiteSec = Math.round(avgSiteSec - normSiteSec);

    const sitesWithWaste = sites.filter((s) => s.has_overflow || s.has_bulky_waste);
    const cleanSites = sites.filter((s) => !s.has_overflow && !s.has_bulky_waste);
    const avgWithWaste = sitesWithWaste.length
      ? sitesWithWaste.reduce((a, s) => a + s.duration_sec, 0) / sitesWithWaste.length
      : 0;
    const avgClean = cleanSites.length
      ? cleanSites.reduce((a, s) => a + s.duration_sec, 0) / cleanSites.length
      : avgSiteSec;
    const hasWasteImpact = sitesWithWaste.length > 0 && avgWithWaste > avgClean + 20;

    const normBinMin = ukvSettings.ukvMin ? parseFloat(ukvSettings.ukvMin) : 18;
    const normBinMax = ukvSettings.ukvMax ? parseFloat(ukvSettings.ukvMax) : 25;

    let totalActiveLoadSec = 0;
    let totalManeuverSec = 0;
    sites.forEach((s) => {
      const dur = Number(s.duration_sec) || 0;
      const act = s.active_loading_sec !== undefined
        ? Number(s.active_loading_sec)
        : Math.min(dur * 0.85, (Number(s.containers_count) || 1) * 21.5);
      totalActiveLoadSec += act;
      totalManeuverSec += Math.max(0, dur - act);
    });

    const avgBinCycleSec = totalBinsEmptied > 0
      ? totalActiveLoadSec / totalBinsEmptied
      : 0;

    const activeLoadPct = totalDurationSec > 0
      ? Math.round((totalActiveLoadSec / totalDurationSec) * 100)
      : 0;
    const idleManeuverPct = totalDurationSec > 0 ? Math.max(0, 100 - activeLoadPct) : 0;

    const overTimedSitesCount = sites.filter((s) => Number(s.duration_sec) > normSiteSec).length;
    const onScheduleSitesCount = Math.max(0, totalSites - overTimedSitesCount);
    const onSchedulePct = Math.round((onScheduleSitesCount / totalSites) * 100);

    return {
      totalSites,
      totalBinsFound,
      totalBinsEmptied,
      totalUnemptiedBins,
      unemptiedSitesList,
      normSiteSec,
      avgSiteSec,
      diffSiteSec,
      hasWasteImpact,
      normBinMin,
      normBinMax,
      avgBinCycleSec,
      activeLoadPct,
      idleManeuverPct,
      totalActiveLoadSec,
      totalManeuverSec,
      onScheduleSitesCount,
      overTimedSitesCount,
      onSchedulePct,
    };
  }, [sites, ukvSettings.sessionMax, ukvSettings.ukvMin, ukvSettings.ukvMax, dataRevision]);

  // =========================================================================
  //        НОВЫЕ АНАЛИТИЧЕСКИЕ ГРАФИКИ ДЛЯ МУСОРОВОЗОВ
  // =========================================================================

  // 1. Почасовая / Посуточная динамика вывоза и Накопительная S-кривая
  const { hourlyChartData, cumulativeSCurve, isHourlyMode, avgBinsPerBucket } = useMemo(() => {
    if (!sites || sites.length === 0) {
      return { hourlyChartData: [], cumulativeSCurve: [], isHourlyMode: true, avgBinsPerBucket: 0 };
    }

    const startDt = data?.date_range?.start ? dayjs(data.date_range.start) : dayjs(sites[0].start);
    const endDt = data?.date_range?.end ? dayjs(data.date_range.end) : dayjs(sites[sites.length - 1].end);
    const diffHours = endDt.diff(startDt, "hour", true);
    const isHourly = diffHours <= 48;

    const bucketsMap = new Map();
    const sortedSites = [...sites].sort((a, b) => new Date(a.start) - new Date(b.start));

    sortedSites.forEach((s) => {
      const d = dayjs(s.start);
      const key = isHourly ? d.format("HH:00") : d.format("DD.MM");
      if (!bucketsMap.has(key)) {
        bucketsMap.set(key, {
          time: key,
          "Выгружено баков": 0,
          "Недогруз (полные)": 0,
          sitesCount: 0,
          violationsCount: 0,
          totalDurationMin: 0,
        });
      }
      const b = bucketsMap.get(key);
      const count = Number(s.containers_count) || 0;
      const unemptied = s.has_unemptied_containers ? (Number(s.unemptied_bins_count) || 1) : 0;
      const emptied = Math.max(0, count - unemptied);

      b["Выгружено баков"] += emptied;
      b["Недогруз (полные)"] += unemptied;
      b.sitesCount += 1;
      b.totalDurationMin = Number((b.totalDurationMin + (Number(s.duration_sec) || 0) / 60).toFixed(1));
      if (s.has_overflow || s.has_bulky_waste || s.has_container_defect || s.has_unemptied_containers) {
        b.violationsCount += 1;
      }
    });

    const chartArr = Array.from(bucketsMap.values());
    const totalEmptied = chartArr.reduce((acc, item) => acc + item["Выгружено баков"], 0);
    const avgBins = chartArr.length > 0 ? Number((totalEmptied / chartArr.length).toFixed(1)) : 0;

    // Строим накопительную S-кривую
    let runBins = 0;
    let runSites = 0;
    const binsCurve = [];
    const sitesCurve = [];

    chartArr.forEach((item) => {
      runBins += item["Выгружено баков"] + item["Недогруз (полные)"];
      runSites += item.sitesCount;
      binsCurve.push({ x: item.time, y: runBins });
      sitesCurve.push({ x: item.time, y: runSites });
    });

    const sCurve = [
      { id: "Вывезено баков", color: "#2eb85c", data: binsCurve },
      { id: "Обслужено КП", color: "#321fdb", data: sitesCurve },
    ];

    return {
      hourlyChartData: chartArr,
      cumulativeSCurve: sCurve,
      isHourlyMode: isHourly,
      avgBinsPerBucket: avgBins,
    };
  }, [sites, data?.date_range, dataRevision]);

  // 2. Баланс времени смены (с разбивкой стоянки на КП на чистую погрузку и маневры)
  const timeBalanceData = useMemo(() => {
    const routeSec = Number(kpis.time_on_route_sec) || 0;
    const activeLoadSec = crewKpis?.totalActiveLoadSec || 0;
    const maneuverSec = crewKpis?.totalManeuverSec || 0;
    const idleSec = Number(kpis.time_downtime_sec) || 0;

    return [
      {
        id: "В движении по маршруту",
        label: "Перегоны",
        value: routeSec,
        color: "#3b82f6",
        desc: "Перемещение мусоровоза между контейнерными площадками и полигоном.",
      },
      {
        id: "Чистая погрузка баков",
        label: "Погрузка",
        value: Math.round(activeLoadSec),
        color: "#2eb85c",
        desc: "Активная работа манипулятора / опрокидывателя по захвату и выгрузке контейнеров.",
      },
      {
        id: "Маневры и подбор на КП",
        label: "Маневры КП",
        value: Math.round(maneuverSec),
        color: "#f9b115",
        desc: "Подъезд к площадке, подкатывание баков вручную, ожидание и выезд с КП.",
      },
      {
        id: "Простой на маршруте",
        label: "Простои",
        value: idleSec,
        color: "#e55353",
        desc: "Длительные остановки вне контейнерных площадок (дольше 5 минут).",
      },
    ];
  }, [kpis, crewKpis]);

  // 3. Структура санитарного состояния КП и статистика по видам нарушений
  const { sanitationPieData, infractionBreakdown } = useMemo(() => {
    if (!sites || sites.length === 0) {
      return { sanitationPieData: [], infractionBreakdown: [] };
    }

    let cleanCount = 0;
    let unemptiedSites = 0;
    let overflowSites = 0;
    let bulkySites = 0;
    let defectSites = 0;

    sites.forEach((s) => {
      const hasAny =
        s.has_unemptied_containers ||
        s.has_overflow ||
        s.has_bulky_waste ||
        s.has_container_defect;

      if (!hasAny) cleanCount += 1;
      if (s.has_unemptied_containers) unemptiedSites += 1;
      if (s.has_overflow) overflowSites += 1;
      if (s.has_bulky_waste) bulkySites += 1;
      if (s.has_container_defect) defectSites += 1;
    });

    const pie = [
      { id: "Чисто (без замечаний)", label: "Чисто", value: cleanCount, color: "#2eb85c" },
      { id: "Недогруз (полные баки)", label: "Недогруз", value: unemptiedSites, color: "#e55353" },
      { id: "Навал ТКО вне баков", label: "Навал ТКО", value: overflowSites, color: "#f97316" },
      { id: "Крупногабарит (КГО)", label: "КГО", value: bulkySites, color: "#eab308" },
      { id: "Дефект контейнера", label: "Дефект бака", value: defectSites, color: "#64748b" },
    ].filter((item) => item.value > 0);

    const breakdown = [
      { label: "Чисто / Вывезено полностью", count: cleanCount, color: "#2eb85c", badge: "success" },
      { label: "Недогруз (оставлены полные баки)", count: unemptiedSites, color: "#e55353", badge: "danger" },
      { label: "Навал ТКО на площадке", count: overflowSites, color: "#f97316", badge: "warning" },
      { label: "Крупногабаритные отходы (КГО)", count: bulkySites, color: "#eab308", badge: "warning" },
      { label: "Дефект контейнера", count: defectSites, color: "#64748b", badge: "secondary" },
    ];

    return { sanitationPieData: pie, infractionBreakdown: breakdown };
  }, [sites, dataRevision]);

  // 4. Распределение КП по количеству установленных контейнеров (Парето ёмкости КП)
  const containersDistributionData = useMemo(() => {
    if (!sites || sites.length === 0) return [];
    const countsMap = {};
    sites.forEach((s) => {
      const c = Number(s.containers_count) || 1;
      countsMap[c] = (countsMap[c] || 0) + 1;
    });

    let cumSites = 0;
    const total = sites.length;

    return Object.keys(countsMap)
      .map(Number)
      .sort((a, b) => a - b)
      .map((binsNum) => {
        cumSites += countsMap[binsNum];
        return {
          binsLabel: `${binsNum} бак.`,
          sitesCount: countsMap[binsNum],
          totalBinsInGroup: binsNum * countsMap[binsNum],
          cumulativePct: Math.round((cumSites / total) * 100),
        };
      });
  }, [sites, dataRevision]);

  const scrollToAnomaly = (index) => {
    setHighlightedUkvIndex(index);
    if (ukvScrollRef.current && ukvData && ukvData.data) {
      const container = ukvScrollRef.current;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const itemsCount = ukvData.data.length;
      const plotWidth = scrollWidth - 70;
      const barCenter = 50 + (plotWidth / itemsCount) * index + (plotWidth / itemsCount / 2);
      const scrollPos = barCenter - clientWidth / 2;
      container.scrollTo({ left: Math.max(0, scrollPos), behavior: "smooth" });
    }
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedUkvIndex(null);
    }, 3000);
  };

  const ukvChartHeight = 350;
  const ukvMarginTop = 30;
  const ukvMarginBottom = 70;
  const ukvInnerHeight = ukvChartHeight - ukvMarginTop - ukvMarginBottom;

  const ukvMaxUkv = ukvData && ukvData.data.length > 0 ? Math.max(1, ...ukvData.data.map((d) => d.ukv)) * 1.2 : 1;
  const ukvMaxSessionMin = ukvData && ukvData.data.length > 0 ? Math.max(1, ...ukvData.data.map((d) => d.durationMin)) * 1.1 : 1;

  const getUkvY = (val) => Math.max(ukvMarginTop, Math.min(ukvMarginTop + ukvInnerHeight, ukvMarginTop + ukvInnerHeight - (val / ukvMaxUkv) * ukvInnerHeight));
  const getSessionY = (val) => Math.max(ukvMarginTop, Math.min(ukvMarginTop + ukvInnerHeight, ukvMarginTop + ukvInnerHeight - (val / ukvMaxSessionMin) * ukvInnerHeight));

  const CustomUkvLayer = ({ bars, innerHeight, width }) => {
    if (!ukvData) return null;
    const uMinY = innerHeight - (ukvData.bounds.uMin / ukvMaxUkv) * innerHeight;
    const uMaxY = innerHeight - (ukvData.bounds.uMax / ukvMaxUkv) * innerHeight;
    const sMinY = innerHeight - (ukvData.bounds.sMin / 60 / ukvMaxSessionMin) * innerHeight;
    const sMaxY = innerHeight - (ukvData.bounds.sMax / 60 / ukvMaxSessionMin) * innerHeight;

    const points = bars.map((bar) => {
      const d = bar.data.data;
      const x = bar.x + bar.width / 2;
      const y = innerHeight - (d.ukv / ukvMaxUkv) * innerHeight;
      return { x, y, d };
    });

    const linePath = points.map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`)).join(" ");

    return (
      <g>
        {ukvSettings.showBars && (
          <>
            <line x1={0} x2={width} y1={sMinY} y2={sMinY} stroke="#2eb85c" strokeWidth={1} strokeDasharray="4 4" opacity={0.8} />
            <line x1={0} x2={width} y1={sMaxY} y2={sMaxY} stroke="#e55353" strokeWidth={1} strokeDasharray="4 4" opacity={0.8} />
          </>
        )}
        {ukvSettings.showLine && (
          <>
            <line x1={0} x2={width} y1={uMinY} y2={uMinY} stroke="#2eb85c" strokeWidth={1} strokeDasharray="4 4" opacity={0.8} />
            <line x1={0} x2={width} y1={uMaxY} y2={uMaxY} stroke="#e55353" strokeWidth={1} strokeDasharray="4 4" opacity={0.8} />
            <path d={linePath} fill="none" stroke="#f9b115" strokeWidth={3} />
            {points.map((p, i) => (
              <g key={`ukv-pt-${i}`}>
                <circle cx={p.x} cy={p.y} r={p.d.isAnomaly ? 6 : 4} fill={p.d.isAnomaly ? "#e55353" : "#f9b115"} stroke="#fff" strokeWidth={2} />
                <text
                  x={p.x}
                  y={p.y - 12}
                  textAnchor="middle"
                  fontSize="11"
                  fill={p.d.isAnomaly ? "#e55353" : "#495057"}
                  fontWeight="bold"
                  style={{ textShadow: "1px 1px 2px #fff, -1px -1px 2px #fff, 1px -1px 2px #fff, -1px 1px 2px #fff" }}
                >
                  {p.d.ukv}с
                </text>
              </g>
            ))}
          </>
        )}
      </g>
    );
  };

  // Кастомный слой линии количества КП поверх столбцов баков
  const HourlySitesLineLayer = ({ bars, innerHeight }) => {
    if (!bars || bars.length === 0) return null;
    const maxSites = Math.max(1, ...hourlyChartData.map((d) => d.sitesCount));

    const pointsMap = new Map();
    bars.forEach((bar) => {
      const timeKey = bar.data.indexValue;
      if (!pointsMap.has(timeKey)) {
        const d = bar.data.data;
        const x = bar.x + bar.width / 2;
        const y = innerHeight - (d.sitesCount / maxSites) * (innerHeight * 0.82);
        pointsMap.set(timeKey, { x, y, count: d.sitesCount });
      }
    });

    const points = Array.from(pointsMap.values());
    if (points.length === 0) return null;

    const linePath = points.map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`)).join(" ");

    return (
      <g>
        <path d={linePath} fill="none" stroke="#321fdb" strokeWidth={3} />
        {points.map((p, i) => (
          <g key={`kp-line-${i}`}>
            <circle cx={p.x} cy={p.y} r={5} fill="#321fdb" stroke="#fff" strokeWidth={2} />
            <text
              x={p.x}
              y={p.y - 10}
              textAnchor="middle"
              fill="#321fdb"
              fontSize="11"
              fontWeight="bold"
              style={{ textShadow: "1px 1px 2px #fff, -1px -1px 2px #fff" }}
            >
              {p.count} КП
            </text>
          </g>
        ))}
      </g>
    );
  };

  const overlayLabelStyle = {
    position: "absolute",
    background: "rgba(255, 255, 255, 0.95)",
    padding: "2px 8px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: "bold",
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    backdropFilter: "blur(3px)",
    transform: "translateY(-50%)",
    zIndex: 10,
    pointerEvents: "none",
  };

  // --- ЛОГИКА LIGHTBOX ---
  const [lightbox, setLightbox] = useState({ isOpen: false, currentIndex: 0 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const galleryItems = useMemo(() => {
    const items = [];
    sites.forEach((site, index) => {
      if (site.photo_before_url) {
        items.push({ url: site.photo_before_url, label: `Фото ДО (КП #${index + 1})`, site });
      }
      if (site.photo_after_url) {
        items.push({ url: site.photo_after_url, label: `Фото ПОСЛЕ (КП #${index + 1})`, site });
      }
    });
    return items;
  }, [sites, dataRevision]);

  const openLightbox = (siteId, typeLabelStr) => {
    const idx = galleryItems.findIndex(
      (item) => item.site.id === siteId && item.label.startsWith(typeLabelStr)
    );
    if (idx !== -1) {
      setLightbox({ isOpen: true, currentIndex: idx });
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
  };

  const closeLightbox = () => {
    setLightbox({ isOpen: false, currentIndex: 0 });
  };

  const nextPhoto = (e) => {
    if (e) e.stopPropagation();
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setLightbox((prev) => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % galleryItems.length,
    }));
  };

  const prevPhoto = (e) => {
    if (e) e.stopPropagation();
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setLightbox((prev) => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + galleryItems.length) % galleryItems.length,
    }));
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightbox.isOpen) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextPhoto();
      if (e.key === "ArrowLeft") prevPhoto();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightbox.isOpen, galleryItems.length]);

  const handlePointerDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  const handlePointerMove = (e) => {
    if (isDragging) {
      setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handlePointerUp = (e) => {
    setIsDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const hasExtendedData = sites.length > 0 || (kpis.time_on_route_sec || 0) > 0;

  return (
    <div style={{ background: "white", padding: "10px" }}>
      {/* 1. ПЛИТКИ ОСНОВНЫХ МЕТРИК */}
      <CRow className="g-3 mb-4">
        <CCol xs={6} md={4} xl={2}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-2">Обслужено КП</div>
              <div className="fs-3 fw-bold text-primary">{crewKpis ? crewKpis.totalSites : (kpis.total_sites_visited || 0)}</div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={6} md={4} xl={2}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-2">Выгружено баков</div>
              <div className="fs-3 fw-bold text-success">
                {crewKpis ? crewKpis.totalBinsEmptied : (kpis.total_containers_emptied || 0)} <span className="fs-6">шт</span>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={6} md={4} xl={2}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-2">С нарушениями</div>
              <div className={`fs-3 fw-bold ${(kpis.violations_count || 0) > 0 ? "text-danger" : "text-dark"}`}>
                {kpis.violations_count || 0} <span className="fs-6">КП</span>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={6} md={4} xl={2}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-2">Недогруз (полные)</div>
              <div className={`fs-3 fw-bold ${(crewKpis ? crewKpis.totalUnemptiedBins : kpis.unemptied_containers_count) > 0 ? "text-danger" : "text-dark"}`}>
                {crewKpis ? crewKpis.totalUnemptiedBins : (kpis.unemptied_containers_count || 0)}
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={6} md={4} xl={2}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-2">Навалы / КГО</div>
              <div className="fs-3 fw-bold text-warning">
                {(kpis.overflow_sites_count || 0) + (kpis.bulky_waste_sites_count || 0)}
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={6} md={4} xl={2}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-2">Пробег GPS</div>
              <div className="fs-3 fw-bold text-info">
                {kpis.total_mileage_km || 0} <span className="fs-6">км</span>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* =========================================================================
                 БЛОК «СВОДНЫЕ KPI ЭКИПАЖА ЗА СМЕНУ» (Задача #423)
         ========================================================================= */}
      {crewKpis && (
        <CCard className="shadow-sm border-0 mb-4">
          <CCardHeader className="bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
            <span className="fw-bold fs-6 text-dark">
              Сводные KPI экипажа за смену
            </span>
            <span className="text-muted small">
              Нормативы синхронизированы с блоком «Нормы времени»
            </span>
          </CCardHeader>
          <CCardBody className="p-0">
            <div className="table-responsive">
              <CTable hover align="middle" className="mb-0">
                <CTableHead color="light">
                  <CTableRow className="text-nowrap">
                    <CTableHeaderCell className="ps-4 py-3" style={{ width: "28%" }}>
                      Метрика
                    </CTableHeaderCell>
                    <CTableHeaderCell className="py-3" style={{ width: "24%" }}>
                      Значение по факту
                    </CTableHeaderCell>
                    <CTableHeaderCell className="py-3" style={{ width: "20%" }}>
                      Норматив / План
                    </CTableHeaderCell>
                    <CTableHeaderCell className="pe-4 py-3" style={{ width: "28%" }}>
                      Отклонение / Статус
                    </CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  <CTableRow>
                    <CTableDataCell className="ps-4 fw-semibold text-dark">
                      Выгружено баков
                    </CTableDataCell>
                    <CTableDataCell className="fw-bold text-dark fs-6">
                      {crewKpis.totalBinsEmptied} из {crewKpis.totalBinsFound} шт
                    </CTableDataCell>
                    <CTableDataCell className="text-muted fw-semibold">
                      100%
                    </CTableDataCell>
                    <CTableDataCell className="pe-4">
                      {crewKpis.totalUnemptiedBins > 0 ? (
                        <span
                          className="fw-bold"
                          style={{ color: "#d97706", cursor: "pointer" }}
                          title="Нажмите для перехода к видео площадки с недогрузом"
                          onClick={() => {
                            const firstUnemptied = crewKpis.unemptiedSitesList[0];
                            if (firstUnemptied && onVideoJump) {
                              onVideoJump(new Date(firstUnemptied.site.start).getTime());
                            }
                          }}
                        >
                          ⚠️ {crewKpis.totalUnemptiedBins}{" "}
                          {crewKpis.totalUnemptiedBins === 1 ? "недогруз" : "недогруза"} (
                          {crewKpis.unemptiedSitesList
                            .map((u) => `КП #${u.siteNumber}`)
                            .join(", ")}
                          )
                        </span>
                      ) : (
                        <span className="text-success fw-bold">
                          🟢 100% выполнено
                        </span>
                      )}
                    </CTableDataCell>
                  </CTableRow>

                  <CTableRow>
                    <CTableDataCell className="ps-4 fw-semibold text-dark">
                      Ср. время на 1 площадку
                    </CTableDataCell>
                    <CTableDataCell className="fw-bold text-dark fs-6">
                      {formatDurationVerbose(crewKpis.avgSiteSec)}
                    </CTableDataCell>
                    <CTableDataCell className="text-muted fw-semibold">
                      {formatDurationVerbose(crewKpis.normSiteSec)}
                    </CTableDataCell>
                    <CTableDataCell className="pe-4">
                      {crewKpis.diffSiteSec > 0 ? (
                        <span className="text-danger fw-bold">
                          🔴 +{formatDuration(crewKpis.diffSiteSec)}{" "}
                          {crewKpis.hasWasteImpact ? "(влияние навалов)" : "(превышение нормы)"}
                        </span>
                      ) : (
                        <span className="text-success fw-bold">
                          🟢 В норме ({crewKpis.diffSiteSec === 0 ? "по плану" : `-${formatDuration(Math.abs(crewKpis.diffSiteSec))}`})
                        </span>
                      )}
                    </CTableDataCell>
                  </CTableRow>

                  <CTableRow>
                    <CTableDataCell className="ps-4 fw-semibold text-dark">
                      Ср. скорость на 1 бак (цикл)
                    </CTableDataCell>
                    <CTableDataCell className="fw-bold text-dark fs-6">
                      {crewKpis.avgBinCycleSec.toFixed(1)} сек / бак
                    </CTableDataCell>
                    <CTableDataCell className="text-muted fw-semibold">
                      {crewKpis.normBinMin}–{crewKpis.normBinMax} сек
                    </CTableDataCell>
                    <CTableDataCell className="pe-4">
                      {crewKpis.avgBinCycleSec > crewKpis.normBinMax ? (
                        <span className="text-danger fw-bold">
                          🔴 +{(crewKpis.avgBinCycleSec - crewKpis.normBinMax).toFixed(1)}с (медленный цикл)
                        </span>
                      ) : crewKpis.avgBinCycleSec < crewKpis.normBinMin && crewKpis.avgBinCycleSec > 0 ? (
                        <span className="text-success fw-bold">
                          🟢 Высокий темп
                        </span>
                      ) : (
                        <span className="text-success fw-bold">
                          🟢 В норме
                        </span>
                      )}
                    </CTableDataCell>
                  </CTableRow>

                  <CTableRow>
                    <CTableDataCell className="ps-4 fw-semibold text-dark">
                      Чистая погрузка vs Простои
                    </CTableDataCell>
                    <CTableDataCell className="fw-bold text-dark fs-6">
                      {crewKpis.activeLoadPct}% / {crewKpis.idleManeuverPct}%
                    </CTableDataCell>
                    <CTableDataCell className="text-muted fw-semibold">
                      80% / 20%
                    </CTableDataCell>
                    <CTableDataCell className="pe-4">
                      {crewKpis.idleManeuverPct > 35 ? (
                        <span className="text-danger fw-bold">
                          🔴 Высокие потери на простои ({crewKpis.idleManeuverPct}%)
                        </span>
                      ) : crewKpis.idleManeuverPct > 20 ? (
                        <span className="fw-bold" style={{ color: "#d97706" }}>
                          🟡 Много задержек на маневры
                        </span>
                      ) : (
                        <span className="text-success fw-bold">
                          🟢 Оптимальный баланс
                        </span>
                      )}
                    </CTableDataCell>
                  </CTableRow>

                  <CTableRow>
                    <CTableDataCell className="ps-4 fw-semibold text-dark border-bottom-0">
                      КП в графике (без аномалий)
                    </CTableDataCell>
                    <CTableDataCell className="fw-bold text-dark fs-6 border-bottom-0">
                      {crewKpis.onScheduleSitesCount} из {crewKpis.totalSites} ({crewKpis.onSchedulePct}%)
                    </CTableDataCell>
                    <CTableDataCell className="text-muted fw-semibold border-bottom-0">
                      &gt; 85%
                    </CTableDataCell>
                    <CTableDataCell className="pe-4 border-bottom-0">
                      {crewKpis.onSchedulePct < 85 ? (
                        <span className="text-danger fw-bold">
                          🔴 {crewKpis.overTimedSitesCount} КП с превышением тайминга
                        </span>
                      ) : (
                        <span className="text-success fw-bold">
                          🟢 В графике ({crewKpis.overTimedSitesCount > 0 ? `${crewKpis.overTimedSitesCount} КП с откл.` : "без отклонений"})
                        </span>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                </CTableBody>
              </CTable>
            </div>
          </CCardBody>
        </CCard>
      )}

      {/* --- КНОПКА РАСКРЫТИЯ / СВОРАЧИВАНИЯ РАСШИРЕННОЙ АНАЛИТИКИ --- */}
      {hasExtendedData && (
        <div className="text-center my-3 d-flex justify-content-center">
          <CButton
            color="primary"
            className="fw-bold shadow-sm d-flex align-items-center justify-content-center text-white"
            style={{ height: "38px", minWidth: "360px", borderRadius: "8px" }}
            onClick={() => setShowExtendedCharts(!showExtendedCharts)}
          >
            <CIcon icon={showExtendedCharts ? cilChevronTop : cilChartLine} className="me-2 text-white" />
            <span>
              {showExtendedCharts
                ? "Скрыть расширенную аналитику маршрута"
                : "Развернуть расширенную аналитику маршрута"}
            </span>
            <CIcon icon={showExtendedCharts ? cilChevronTop : cilChevronBottom} className="ms-2 text-white" />
          </CButton>
        </div>
      )}

      {/* =========================================================================
                 БЛОК РАСШИРЕННЫХ ГРАФИКОВ МУСОРОВОЗА
         ========================================================================= */}
      {showExtendedCharts && hasExtendedData && (
        <div className="mt-3">
          {/* РЯД 1: ПОЧАСОВАЯ ДИНАМИКА ВЫВОЗА И НАКОПИТЕЛЬНЫЙ ТЕМП (S-КРИВАЯ) */}
          {hourlyChartData.length > 0 && (
            <CRow className="mb-4 g-3">
              {/* График 1: Почасовая динамика вывоза баков и посещения КП */}
              <CCol xs={12} lg={7}>
                <CCard className="shadow-sm border-0 h-100">
                  <CCardHeader className="bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <span className="fw-bold">
                      Динамика вывоза ({isHourlyMode ? "по часам" : "по дням"})
                    </span>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                      <CBadge color="success" shape="rounded-pill">Выгружено баков</CBadge>
                      <CBadge color="danger" shape="rounded-pill">Недогруз</CBadge>
                      <CBadge color="primary" shape="rounded-pill">Площадок (КП)</CBadge>
                    </div>
                  </CCardHeader>
                  <CCardBody className="p-2" style={{ height: "340px" }}>
                    <ResponsiveBar
                      data={hourlyChartData}
                      keys={["Выгружено баков", "Недогруз (полные)"]}
                      indexBy="time"
                      margin={{ top: 35, right: 25, bottom: 55, left: 50 }}
                      padding={0.35}
                      colors={({ id }) => (id === "Выгружено баков" ? "#2eb85c" : "#e55353")}
                      borderRadius={3}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: hourlyChartData.length > 10 ? -35 : 0,
                        legend: isHourlyMode ? "Часы смены" : "Дни",
                        legendPosition: "middle",
                        legendOffset: 42,
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        legend: "Контейнеров (шт)",
                        legendPosition: "middle",
                        legendOffset: -40,
                      }}
                      labelSkipHeight={14}
                      labelTextColor="#ffffff"
                      layers={["grid", "axes", "bars", HourlySitesLineLayer, "markers"]}
                      markers={
                        avgBinsPerBucket > 0
                          ? [
                              {
                                axis: "y",
                                value: avgBinsPerBucket,
                                lineStyle: { stroke: "#2eb85c", strokeWidth: 1.5, strokeDasharray: "4 4" },
                                legend: `Ср. темп: ${avgBinsPerBucket} бак.`,
                                legendPosition: "top-left",
                                textStyle: { fill: "#2eb85c", fontSize: 11, fontWeight: "bold" },
                              },
                            ]
                          : []
                      }
                      tooltip={({ data: row }) => (
                        <div
                          style={{
                            padding: "10px 12px",
                            background: "white",
                            border: "1px solid #ccc",
                            borderRadius: "6px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                            minWidth: "200px",
                          }}
                        >
                          <div className="fw-bold border-bottom pb-1 mb-2 text-center">
                            Период: {row.time}
                          </div>
                          <div className="d-flex justify-content-between small mb-1">
                            <span className="text-muted">Обслужено площадок:</span>
                            <strong className="text-primary">{row.sitesCount} КП</strong>
                          </div>
                          <div className="d-flex justify-content-between small mb-1">
                            <span className="text-muted">Выгружено баков:</span>
                            <strong className="text-success">{row["Выгружено баков"]} шт.</strong>
                          </div>
                          {row["Недогруз (полные)"] > 0 && (
                            <div className="d-flex justify-content-between small mb-1">
                              <span className="text-muted">Оставлено полных:</span>
                              <strong className="text-danger">{row["Недогруз (полные)"]} шт.</strong>
                            </div>
                          )}
                          <div className="d-flex justify-content-between small border-top pt-1 mt-1">
                            <span className="text-muted">Время на КП:</span>
                            <strong>{row.totalDurationMin} мин</strong>
                          </div>
                        </div>
                      )}
                    />
                  </CCardBody>
                </CCard>
              </CCol>

              {/* График 2: Накопительный итог вывоза (S-кривая) */}
              <CCol xs={12} lg={5}>
                <CCard className="shadow-sm border-0 h-100">
                  <CCardHeader className="bg-white py-3 d-flex justify-content-between align-items-center">
                    <span className="fw-bold">Накопительный темп вывоза</span>
                    <span className="text-muted small">Прогресс смены</span>
                  </CCardHeader>
                  <CCardBody className="p-2" style={{ height: "340px" }}>
                    <ResponsiveLine
                      data={cumulativeSCurve}
                      margin={{ top: 35, right: 25, bottom: 55, left: 50 }}
                      xScale={{ type: "point" }}
                      yScale={{ type: "linear", min: 0, max: "auto", stacked: false }}
                      curve="monotoneX"
                      colors={({ id }) => (id.includes("баков") ? "#2eb85c" : "#321fdb")}
                      lineWidth={3}
                      pointSize={7}
                      pointBorderWidth={2}
                      pointBorderColor="#fff"
                      enableArea={true}
                      areaOpacity={0.12}
                      useMesh={true}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: hourlyChartData.length > 10 ? -35 : 0,
                        legend: isHourlyMode ? "Часы смены" : "Дни",
                        legendPosition: "middle",
                        legendOffset: 42,
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        legend: "Накопительно (шт)",
                        legendPosition: "middle",
                        legendOffset: -40,
                      }}
                      legends={[
                        {
                          anchor: "top-left",
                          direction: "row",
                          translateY: -28,
                          itemWidth: 165,
                          itemHeight: 18,
                          symbolSize: 10,
                          symbolShape: "circle",
                        },
                      ]}
                      tooltip={({ point }) => (
                        <div
                          style={{
                            padding: "8px 12px",
                            background: "white",
                            border: `2px solid ${point.serieColor}`,
                            borderRadius: "6px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                          }}
                        >
                          <div className="small text-muted">{point.serieId}</div>
                          <div className="fw-bold text-dark">
                            К {point.data.xFormatted}:{" "}
                            <span style={{ color: point.serieColor }}>{point.data.yFormatted} шт.</span>
                          </div>
                        </div>
                      )}
                    />
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          )}

          {/* РЯД 2: БАЛАНС ВРЕМЕНИ СМЕНЫ | КАЧЕСТВО УБОРКИ И НАРУШЕНИЯ | ПАРЕТО ЁМКОСТИ КП */}
          <CRow className="mb-4 g-3">
            {/* График 3: Баланс времени смены */}
            <CCol xs={12} lg={4}>
              <CCard className="shadow-sm border-0 h-100">
                <CCardHeader className="bg-white py-3 fw-bold text-center">
                  Баланс времени смены
                </CCardHeader>
                <CCardBody className="p-2" style={{ height: "320px" }}>
                  <ResponsiveBar
                    data={timeBalanceData}
                    keys={["value"]}
                    indexBy="label"
                    margin={{ top: 20, right: 20, bottom: 45, left: 55 }}
                    padding={0.35}
                    colors={({ data: d }) => d.color}
                    colorBy="indexValue"
                    borderRadius={4}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      legend: "Время (мин)",
                      legendPosition: "middle",
                      legendOffset: -45,
                      format: (v) => `${Math.round(v / 60)}м`,
                    }}
                    enableLabel={false}
                    tooltip={({ data: d }) => (
                      <div
                        style={{
                          padding: "10px",
                          background: "white",
                          border: `2px solid ${d.color}`,
                          borderRadius: "6px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          maxWidth: "240px",
                        }}
                      >
                        <div className="fw-bold mb-1" style={{ color: d.color }}>
                          {d.id}
                        </div>
                        <div className="fs-6 fw-bold text-dark mb-1">
                          {formatDuration(d.value)}
                        </div>
                        <div className="text-muted" style={{ fontSize: "0.75rem", lineHeight: 1.3 }}>
                          {d.desc}
                        </div>
                      </div>
                    )}
                  />
                </CCardBody>
              </CCard>
            </CCol>

            {/* График 4: Санитарное состояние КП и структура нарушений */}
            <CCol xs={12} lg={4}>
              <CCard className="shadow-sm border-0 h-100">
                <CCardHeader className="bg-white py-3 fw-bold text-center">
                  Санитарное состояние площадок
                </CCardHeader>
                <CCardBody className="p-3 d-flex flex-column justify-content-between" style={{ minHeight: "320px" }}>
                  {sanitationPieData.length > 0 ? (
                    <>
                      <div style={{ height: "190px", position: "relative" }}>
                        <ResponsivePie
                          data={sanitationPieData}
                          margin={{ top: 10, right: 15, bottom: 10, left: 15 }}
                          innerRadius={0.65}
                          padAngle={2.5}
                          cornerRadius={4}
                          colors={{ datum: "data.color" }}
                          borderWidth={1}
                          borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                          enableArcLinkLabels={false}
                          enableArcLabels={true}
                          arcLabel={(d) => `${d.value}`}
                          arcLabelsSkipAngle={12}
                          arcLabelsTextColor="#ffffff"
                        />
                        {/* Сводка в центре пончика */}
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            textAlign: "center",
                            pointerEvents: "none",
                          }}
                        >
                          <div className="fs-4 fw-bold text-dark lh-1">
                            {sites.length}
                          </div>
                          <div className="text-muted fw-semibold" style={{ fontSize: "0.7rem" }}>
                            всего КП
                          </div>
                        </div>
                      </div>

                      <div className="border-top pt-2 mt-2">
                        {infractionBreakdown.map((item, i) => (
                          <div key={i} className="d-flex justify-content-between align-items-center small mb-1">
                            <span className="d-flex align-items-center text-dark text-truncate me-2" title={item.label}>
                              <span
                                style={{
                                  width: "10px",
                                  height: "10px",
                                  borderRadius: "50%",
                                  backgroundColor: item.color,
                                  display: "inline-block",
                                  marginRight: "8px",
                                  flexShrink: 0,
                                }}
                              />
                              <span className="text-truncate">{item.label}</span>
                            </span>
                            <CBadge color={item.badge} shape="rounded-pill" className="px-2 flex-shrink-0">
                              {item.count} КП
                            </CBadge>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                      Нет данных по площадкам
                    </div>
                  )}
                </CCardBody>
              </CCard>
            </CCol>

            {/* График 5: Распределение КП по количеству контейнеров (Парето) */}
            <CCol xs={12} lg={4}>
              <CCard className="shadow-sm border-0 h-100">
                <CCardHeader className="bg-white py-3 fw-bold text-center">
                  Ёмкость площадок (Кол-во баков на КП)
                </CCardHeader>
                <CCardBody className="p-2" style={{ height: "320px" }}>
                  {containersDistributionData.length > 0 ? (
                    <ResponsiveBar
                      data={containersDistributionData}
                      keys={["sitesCount"]}
                      indexBy="binsLabel"
                      margin={{ top: 20, right: 20, bottom: 45, left: 50 }}
                      padding={0.35}
                      colors={["#6610f2"]}
                      borderRadius={4}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        legend: "Размер площадки (баков на КП)",
                        legendPosition: "middle",
                        legendOffset: 36,
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        legend: "Количество КП",
                        legendPosition: "middle",
                        legendOffset: -38,
                      }}
                      labelSkipHeight={14}
                      labelTextColor="#ffffff"
                      tooltip={({ data: d }) => (
                        <div
                          style={{
                            padding: "8px 12px",
                            background: "white",
                            border: "1px solid #6610f2",
                            borderRadius: "6px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                          }}
                        >
                          <div className="fw-bold text-dark">Площадки по {d.binsLabel}</div>
                          <div className="small text-muted">
                            Таких площадок: <strong className="text-dark">{d.sitesCount} шт.</strong>
                          </div>
                          <div className="small text-muted">
                            Суммарно баков: <strong className="text-success">{d.totalBinsInGroup} шт.</strong>
                          </div>
                          <div className="small text-muted">
                            Накопительная доля: <strong>{d.cumulativePct}% маршрута</strong>
                          </div>
                        </div>
                      )}
                    />
                  ) : (
                    <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                      Нет данных
                    </div>
                  )}
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>

          {/* ================= ГРАФИК ЭФФЕКТИВНОСТИ ОБСЛУЖИВАНИЯ (УКВ) ================= */}
          {ukvData && ukvData.data.length > 0 && (
            <CRow className="mb-4">
              <CCol xs={12}>
                <CCard className="shadow-sm border-0 h-100">
                  <CCardHeader className="bg-white py-3 border-bottom d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                    <div className="d-flex align-items-center">
                      <span className="fw-bold fs-6 text-dark me-2">
                        Разбор времени обслуживания площадок (Фазы работы)
                      </span>
                      <CTooltip content="Задайте допустимые диапазоны времени для выявления аномалий. Если поля оставить пустыми, система использует стандартные нормативы (18–25 сек на бак и до 240 сек на площадку).">
                        <span>
                          <CIcon icon={cilInfo} className="text-info" style={{ cursor: "help" }} />
                        </span>
                      </CTooltip>
                    </div>

                    <div className="d-flex align-items-center gap-3 bg-light px-3 py-2 rounded-3 border">
                      <div className="form-check form-switch mb-0 d-flex align-items-center">
                        <input
                          className="form-check-input me-2 m-0"
                          type="checkbox"
                          id="showLine"
                          checked={ukvSettings.showLine}
                          onChange={(e) => setUkvSettings((p) => ({ ...p, showLine: e.target.checked }))}
                          style={{ cursor: "pointer" }}
                        />
                        <label className="form-check-label small fw-bold text-dark" htmlFor="showLine" style={{ cursor: "pointer" }}>
                          Ср. захват 1 бака (Линия)
                        </label>
                      </div>

                      <div className="vr opacity-25" style={{ height: "18px" }}></div>

                      <div className="form-check form-switch mb-0 d-flex align-items-center">
                        <input
                          className="form-check-input me-2 m-0"
                          type="checkbox"
                          id="showBars"
                          checked={ukvSettings.showBars}
                          onChange={(e) => setUkvSettings((p) => ({ ...p, showBars: e.target.checked }))}
                          style={{ cursor: "pointer" }}
                        />
                        <label className="form-check-label small fw-bold text-dark" htmlFor="showBars" style={{ cursor: "pointer" }}>
                          Время на КП (Столбцы)
                        </label>
                      </div>
                    </div>
                  </CCardHeader>

                  <CCardBody className="p-3">
                    <div className="bg-light bg-opacity-75 border rounded-3 p-3 mb-3">
                      <div className="d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge bg-secondary bg-opacity-10 text-secondary border px-2 py-1 fw-bold text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                            Нормы времени
                          </span>
                          <span className="small text-muted">(допустимый диапазон «от — до»):</span>
                        </div>

                        <div className="d-flex flex-wrap align-items-center gap-3">
                          <div className="d-flex align-items-center bg-white border rounded-3 px-3 py-1 shadow-sm gap-2">
                            <span className="fw-bold small text-dark text-nowrap">⏱️ Захват 1 бака:</span>
                            <div className="d-flex align-items-center gap-1">
                              <span className="text-muted small">от</span>
                              <input
                                type="number"
                                className="form-control form-control-sm text-center fw-bold border-success"
                                style={{ width: "65px", color: "#2eb85c" }}
                                placeholder={(ukvData?.bounds.uMin || 18).toFixed(0)}
                                value={ukvSettings.ukvMin}
                                onChange={(e) => setUkvSettings((p) => ({ ...p, ukvMin: e.target.value }))}
                              />
                              <span className="text-muted small px-1">—</span>
                              <span className="text-muted small">до</span>
                              <input
                                type="number"
                                className="form-control form-control-sm text-center fw-bold border-danger"
                                style={{ width: "65px", color: "#e55353" }}
                                placeholder={(ukvData?.bounds.uMax || 25).toFixed(0)}
                                value={ukvSettings.ukvMax}
                                onChange={(e) => setUkvSettings((p) => ({ ...p, ukvMax: e.target.value }))}
                              />
                              <span className="text-muted small ms-1">сек</span>
                            </div>
                          </div>

                          <div className="d-flex align-items-center bg-white border rounded-3 px-3 py-1 shadow-sm gap-2">
                            <span className="fw-bold small text-dark text-nowrap">🚛 Время на КП:</span>
                            <div className="d-flex align-items-center gap-1">
                              <span className="text-muted small">от</span>
                              <input
                                type="number"
                                className="form-control form-control-sm text-center fw-bold border-success"
                                style={{ width: "65px", color: "#2eb85c" }}
                                placeholder={(ukvData?.bounds.sMin || 60).toFixed(0)}
                                value={ukvSettings.sessionMin}
                                onChange={(e) => setUkvSettings((p) => ({ ...p, sessionMin: e.target.value }))}
                              />
                              <span className="text-muted small px-1">—</span>
                              <span className="text-muted small">до</span>
                              <input
                                type="number"
                                className="form-control form-control-sm text-center fw-bold border-danger"
                                style={{ width: "65px", color: "#e55353" }}
                                placeholder={(ukvData?.bounds.sMax || 240).toFixed(0)}
                                value={ukvSettings.sessionMax}
                                onChange={(e) => setUkvSettings((p) => ({ ...p, sessionMax: e.target.value }))}
                              />
                              <span className="text-muted small ms-1">сек</span>
                            </div>
                          </div>

                          {(ukvSettings.ukvMin || ukvSettings.ukvMax || ukvSettings.sessionMin || ukvSettings.sessionMax) && (
                            <CButton
                              color="secondary"
                              variant="outline"
                              size="sm"
                              className="fw-semibold px-2 py-1"
                              onClick={() => setUkvSettings({ ...ukvSettings, ukvMin: "", ukvMax: "", sessionMin: "", sessionMax: "" })}
                            >
                              Сбросить
                            </CButton>
                          )}
                        </div>
                      </div>
                    </div>

                    {ukvData.anomalies.length > 0 ? (
                      <div className="alert alert-danger py-2 d-flex align-items-center flex-wrap gap-2 mb-3 border-danger border-opacity-50">
                        <CIcon icon={cilWarning} className="text-danger flex-shrink-0" />
                        <span className="fw-bold small me-2">
                          Аномалий: {ukvData.anomalies.length}. Нажмите для перехода:
                        </span>
                        {ukvData.anomalies.slice(0, 8).map((anom, i) => (
                          <CBadge
                            key={i}
                            color="danger"
                            shape="rounded-pill"
                            style={{ cursor: "pointer" }}
                            onClick={() => scrollToAnomaly(anom.index)}
                            title={anom.anomalyReasons.join("\n")}
                          >
                            {anom.displayLabel}
                          </CBadge>
                        ))}
                        {ukvData.anomalies.length > 8 && (
                          <span className="small text-muted fw-bold">+{ukvData.anomalies.length - 8} еще</span>
                        )}
                      </div>
                    ) : (
                      <div className="alert alert-success py-2 d-flex align-items-center mb-3">
                        <span className="fw-bold small">Отклонений не выявлено. Обслуживание в пределах норм.</span>
                      </div>
                    )}

                    <div style={{ position: "relative", width: "100%", border: "1px solid #ebedef", borderRadius: "8px", overflow: "hidden" }}>
                      <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 10 }}>
                        {ukvSettings.showBars && (
                          <>
                            <div style={{ ...overlayLabelStyle, top: getSessionY(ukvData.bounds.sMin / 60), left: "60px", color: "#2eb85c" }}>
                              Мин. время КП ({(ukvData.bounds.sMin / 60).toFixed(1)}м)
                            </div>
                            <div style={{ ...overlayLabelStyle, top: getSessionY(ukvData.bounds.sMax / 60), left: "60px", color: "#e55353" }}>
                              Макс. время КП ({(ukvData.bounds.sMax / 60).toFixed(1)}м)
                            </div>
                          </>
                        )}
                        {ukvSettings.showLine && (
                          <>
                            <div style={{ ...overlayLabelStyle, top: getUkvY(ukvData.bounds.uMin), right: "20px", color: "#2eb85c" }}>
                              Захват Мин ({ukvData.bounds.uMin.toFixed(0)}с)
                            </div>
                            <div style={{ ...overlayLabelStyle, top: getUkvY(ukvData.bounds.uMax), right: "20px", color: "#e55353" }}>
                              Захват Макс ({ukvData.bounds.uMax.toFixed(0)}с)
                            </div>
                          </>
                        )}
                      </div>

                      <div ref={ukvScrollRef} style={{ width: "100%", overflowX: "auto", overflowY: "hidden", position: "relative" }}>
                        <div style={{ height: `${ukvChartHeight}px`, width: Math.max(100, ukvData.data.length * 6) + "%", minWidth: "100%" }}>
                          <ResponsiveBar
                            data={ukvData.data.map((d) => ({ ...d, isHighlighted: d.index === highlightedUkvIndex }))}
                            keys={["durationMin"]}
                            indexBy="timeLabel"
                            margin={{ top: ukvMarginTop, right: 20, bottom: ukvMarginBottom, left: 50 }}
                            padding={0.4}
                            yScale={{ type: "linear", max: ukvMaxSessionMin }}
                            colors={({ data: d }) => {
                              if (!ukvSettings.showBars) return "transparent";
                              return d.isHighlighted ? "#e55353" : "rgba(50, 31, 219, 0.6)";
                            }}
                            borderRadius={4}
                            axisTop={null}
                            axisRight={null}
                            axisBottom={{
                              tickSize: 5,
                              tickPadding: 5,
                              tickRotation: -45,
                              legend: "Хронология обслуживания площадок",
                              legendPosition: "middle",
                              legendOffset: 60,
                              format: (v) => v.split(" (КП")[0],
                            }}
                            axisLeft={{
                              tickSize: 5,
                              tickPadding: 5,
                              tickRotation: 0,
                              legend: "Общее время на КП (мин)",
                              legendPosition: "middle",
                              legendOffset: -40,
                            }}
                            enableGridY={true}
                            labelSkipHeight={12}
                            labelTextColor={ukvSettings.showBars ? "#ffffff" : "transparent"}
                            layers={["grid", "axes", "bars", CustomUkvLayer]}
                            onClick={(node) => {
                              if (typeof onVideoJump === "function" && node.data.startMs) {
                                window.scrollTo({ top: 0, behavior: "smooth" });
                                onVideoJump(node.data.startMs);
                              }
                            }}
                            theme={{ tooltip: { container: { cursor: "pointer" } } }}
                            tooltip={({ data: d }) => {
                              const isSessAnom = d.durationSec < ukvData.bounds.sMin || d.durationSec > ukvData.bounds.sMax;
                              const isUkvAnom = d.ukv < ukvData.bounds.uMin || d.ukv > ukvData.bounds.uMax;

                              return (
                                <div style={{ padding: "10px", background: "white", border: `2px solid ${d.isAnomaly ? "#e55353" : "#2eb85c"}`, borderRadius: "6px", boxShadow: "0 4px 15px rgba(0,0,0,0.1)", cursor: "pointer" }}>
                                  <div className="fw-bold border-bottom pb-1 mb-2 text-center text-dark">
                                    {d.displayLabel} <span className="text-muted fw-normal">| {d.title}</span>
                                  </div>
                                  <div className="small d-flex justify-content-between mb-1">
                                    <span className="text-muted">Время обслуживания КП:</span>
                                    <strong className={`ms-3 ${isSessAnom ? "text-danger" : "text-dark"}`}>{formatDuration(d.durationSec)}</strong>
                                  </div>
                                  <div className="small d-flex justify-content-between mb-1">
                                    <span className="text-muted">Баков на площадке:</span>
                                    <strong className="ms-3">{d.cycles} шт.</strong>
                                  </div>
                                  <div className="small d-flex justify-content-between mt-2 pt-2 border-top">
                                    <span className="fw-bold">Цикл 1 бака (чистая погрузка):</span>
                                    <strong className={`ms-3 fs-6 ${isUkvAnom ? "text-danger" : "text-success"}`}>
                                      {d.ukv} сек.
                                    </strong>
                                  </div>
                                  {d.anomalyReasons.length > 0 && (
                                    <div className="mt-2 pt-2 border-top">
                                      {d.anomalyReasons.map((r, idx) => (
                                        <div key={idx} className="text-danger small fw-bold" style={{ fontSize: "0.7rem" }}>- {r}</div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          )}
        </div>
      )}

      {/* 2. ТАЙМЛАЙН */}
      <div className="mb-4">
        {timelineComponent}
      </div>

      {/* 3. ЖУРНАЛ КОНТЕЙНЕРНЫХ ПЛОЩАДОК */}
      <CCard className="shadow-sm border-0 mb-4">
        <CCardHeader className="bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
          <span>Журнал обслуживания контейнерных площадок</span>
          <span className="badge bg-secondary rounded-pill">{sites.length} объектов</span>
        </CCardHeader>
        <CCardBody className="p-0">
          <div className="table-responsive" style={{ maxHeight: "60vh", overflowY: "auto" }}>
            <CTable hover align="middle" className="mb-0" small>
              <CTableHead color="light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <CTableRow className="align-middle text-nowrap">
                  <CTableHeaderCell className="text-center ps-3" style={{ width: "50px" }}>#</CTableHeaderCell>
                  <CTableHeaderCell className="text-start" style={{ width: "170px" }}>Время стоянки</CTableHeaderCell>
                  <CTableHeaderCell className="text-start" style={{ minWidth: "220px" }}>Площадка / Адрес</CTableHeaderCell>
                  <CTableHeaderCell className="text-center" style={{ width: "120px" }}>Длительность</CTableHeaderCell>
                  <CTableHeaderCell className="text-center" style={{ width: "120px" }}>Контейнеров</CTableHeaderCell>
                  <CTableHeaderCell className="text-start" style={{ minWidth: "230px" }}>Статус и нарушения ИИ</CTableHeaderCell>
                  <CTableHeaderCell className="text-center" style={{ width: "140px" }}>Фото (До / После)</CTableHeaderCell>
                  <CTableHeaderCell className="text-center" style={{ width: "90px" }}>Видео</CTableHeaderCell>
                  <CTableHeaderCell className="text-center pe-3" style={{ width: "80px" }}>Действия</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {sites.map((site, idx) => (
                  <CTableRow key={site.id} className="align-middle">
                    <CTableDataCell className="text-center ps-3 fw-bold text-muted">{idx + 1}</CTableDataCell>
                    
                    <CTableDataCell className="text-start">
                      <div className="fw-bold text-dark">{dayjs(site.start).format("DD.MM.YYYY")}</div>
                      <div className="small text-muted">
                        {dayjs(site.start).format("HH:mm:ss")} — {dayjs(site.end).format("HH:mm:ss")}
                      </div>
                    </CTableDataCell>

                    <CTableDataCell className="text-start">
                      <div className="fw-bold text-dark text-truncate" style={{ maxWidth: "260px" }} title={site.address || "Адрес не определен"}>
                        <CIcon icon={cilMap} className="text-danger me-1 flex-shrink-0" size="sm" />
                        <span>{site.address || "Адрес не определен"}</span>
                      </div>
                      {site.latitude > 0 && site.longitude > 0 ? (
                        <a
                          href={`https://yandex.ru/maps/?pt=${site.longitude},${site.latitude}&z=17&l=map`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none small text-muted d-inline-flex align-items-center mt-1"
                          onClick={(e) => e.stopPropagation()}
                          title="Открыть точку на Яндекс.Картах"
                        >
                          <span style={{ fontSize: "0.75rem" }}>
                            {site.latitude.toFixed(5)}, {site.longitude.toFixed(5)} ↗
                          </span>
                        </a>
                      ) : (
                        <span className="small text-muted" style={{ fontSize: "0.75rem" }}>
                          GPS не определен
                        </span>
                      )}
                    </CTableDataCell>

                    <CTableDataCell className="text-center fw-semibold text-dark">
                      {formatDuration(site.duration_sec)}
                    </CTableDataCell>

                    <CTableDataCell className="text-center">
                      <span className="badge bg-success fs-6 px-3 py-1 rounded-pill shadow-sm">
                        {site.containers_count} бак.
                      </span>
                    </CTableDataCell>

                    <CTableDataCell className="text-start">
                      <div className="d-flex flex-wrap gap-1 align-items-center">
                        {site.has_unemptied_containers && (
                          <CBadge color="danger" className="fw-bold">
                            🚨 Недогруз: остались полные баки
                          </CBadge>
                        )}
                        {site.has_overflow && <CBadge color="danger">Навал ТКО</CBadge>}
                        {site.has_bulky_waste && <CBadge color="warning" textColor="dark">КГО на площадке</CBadge>}
                        {site.has_container_defect && <CBadge color="dark">Дефект бака</CBadge>}
                        
                        {site.llm_analysis && (
                          <CBadge
                            color="info"
                            className="p-1 px-2 text-white shadow-sm d-inline-flex align-items-center"
                            style={{ cursor: "pointer", background: "linear-gradient(45deg, #20a8d8, #6610f2)" }}
                            onClick={() => setSelectedLlmSite(site)}
                            title="Нажмите для просмотра полного заключения ИИ"
                          >
                            🤖 {site.llm_analysis.waste_category}: ~{site.llm_analysis.estimated_volume_m3} м³ ℹ️
                          </CBadge>
                        )}

                        {!site.has_overflow &&
                          !site.has_bulky_waste &&
                          !site.has_container_defect &&
                          !site.has_unemptied_containers && (
                            <span className="text-success small fw-bold d-inline-flex align-items-center">
                              <CIcon icon={cilCheckCircle} className="me-1" /> Чисто / Без нарушений
                            </span>
                          )}
                      </div>
                    </CTableDataCell>

                    {/* ФОТО ДО / ПОСЛЕ */}
                    <CTableDataCell className="text-center">
                      <div className="d-flex justify-content-center align-items-center gap-2">
                        {site.photo_before_url ? (
                          <img
                            src={site.photo_before_url}
                            alt="До"
                            style={{
                              width: "48px",
                              height: "36px",
                              objectFit: "cover",
                              borderRadius: "4px",
                              cursor: "pointer",
                              border: "1px solid #ccc",
                            }}
                            onClick={() => openLightbox(site.id, "Фото ДО")}
                            title="Смотреть Фото ДО"
                          />
                        ) : (
                          <span className="text-muted small">-</span>
                        )}

                        {site.photo_after_url ? (
                          <img
                            src={site.photo_after_url}
                            alt="После"
                            style={{
                              width: "48px",
                              height: "36px",
                              objectFit: "cover",
                              borderRadius: "4px",
                              cursor: "pointer",
                              border: "1px solid #2eb85c",
                            }}
                            onClick={() => openLightbox(site.id, "Фото ПОСЛЕ")}
                            title="Смотреть Фото ПОСЛЕ"
                          />
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </div>
                    </CTableDataCell>

                    {/* КНОПКА ВИДЕО */}
                    <CTableDataCell className="text-center">
                      <CButton
                        size="sm"
                        color="primary"
                        variant="outline"
                        className="fw-bold"
                        onClick={() => onVideoJump(new Date(site.start).getTime())}
                      >
                        <CIcon icon={cilMediaPlay} className="me-1" /> Кадр
                      </CButton>
                    </CTableDataCell>

                    {/* ДЕЙСТВИЯ: РЕДАКТИРОВАНИЕ И УДАЛЕНИЕ */}
                    <CTableDataCell className="text-center pe-3">
                      <div className="d-flex justify-content-center gap-1">
                        <CButton
                          size="sm"
                          color="secondary"
                          variant="ghost"
                          title="Редактировать параметры площадки"
                          onClick={() => handleOpenEdit(site)}
                        >
                          <CIcon icon={cilPencil} size="sm" />
                        </CButton>
                        <CButton
                          size="sm"
                          color="danger"
                          variant="ghost"
                          title="Удалить заезд (из журнала и таймлайна)"
                          onClick={() => {
                            setSessionToDelete(site);
                            setDeleteModalVisible(true);
                          }}
                        >
                          <CIcon icon={cilTrash} size="sm" />
                        </CButton>
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                ))}

                {sites.length === 0 && (
                  <CTableRow>
                    <CTableDataCell colSpan={9} className="text-center p-5 text-muted">
                      Стоянок обслуживания за выбранный период не зафиксировано
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>
          </div>
        </CCardBody>
      </CCard>

      {/* КНОПКА ПЕЧАТИ / PDF «ВЫЕЗД ЗА КГО» */}
      <div className="d-flex justify-content-end mt-3 mb-4">
        <CButton
          color="danger"
          size="lg"
          className="fw-bold px-4 py-2 text-white shadow-sm d-flex align-items-center gap-2"
          onClick={() =>
            printKgoActHtml({
              sites,
              dateRange: data?.date_range,
              deviceName: data?.device_name || "Мусоровоз",
              orgName: data?.organization_name,
            })
          }
        >
          <CIcon icon={cilFile} size="lg" />
          Выезд за КГО
        </CButton>
      </div>

      {/* МОДАЛЬНОЕ ОКНО РЕДАКТИРОВАНИЯ ДАННЫХ КП */}
      {editingSession && (
        <CModal
          size="lg"
          visible={editModalVisible}
          onClose={() => setEditModalVisible(false)}
          alignment="center"
        >
          <CModalHeader className="bg-primary text-white">
            <CModalTitle className="fw-bold d-flex align-items-center justify-content-between w-100 pe-2">
              <span>Редактирование и оценка состояния КП</span>
              {editingSession.start && (
                <span className="badge bg-white text-primary fw-bold" style={{ fontSize: "0.8rem" }}>
                  {dayjs(editingSession.start).format("DD.MM.YYYY HH:mm:ss")}
                </span>
              )}
            </CModalTitle>
          </CModalHeader>
          <CModalBody className="p-4">
            <div className="row g-4">
              <div className="col-12 col-lg-6 d-flex flex-column">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-bold small text-uppercase text-muted">
                    Визуальный контроль площадки:
                  </span>
                  {editingSession.photo_before_url && editingSession.photo_after_url && (
                    <div className="btn-group btn-group-sm">
                      <CButton
                        color={editingSession.previewMode === "after" ? "success" : "secondary"}
                        variant={editingSession.previewMode === "after" ? undefined : "outline"}
                        className="fw-bold py-0 px-2"
                        onClick={() => setEditingSession({ ...editingSession, previewMode: "after" })}
                      >
                        Фото ПОСЛЕ
                      </CButton>
                      <CButton
                        color={editingSession.previewMode === "before" ? "primary" : "secondary"}
                        variant={editingSession.previewMode === "before" ? undefined : "outline"}
                        className="fw-bold py-0 px-2"
                        onClick={() => setEditingSession({ ...editingSession, previewMode: "before" })}
                      >
                        Фото ДО
                      </CButton>
                    </div>
                  )}
                </div>

                <div
                  className="bg-dark rounded border d-flex align-items-center justify-content-center position-relative overflow-hidden shadow-sm flex-grow-1"
                  style={{ minHeight: "260px", maxHeight: "340px" }}
                >
                  {(() => {
                    const activePhotoUrl =
                      editingSession.previewMode === "before"
                        ? editingSession.photo_before_url || editingSession.photo_after_url
                        : editingSession.photo_after_url || editingSession.photo_before_url;

                    const isShowingAfter =
                      (editingSession.previewMode === "after" && editingSession.photo_after_url) ||
                      !editingSession.photo_before_url;

                    if (!activePhotoUrl) {
                      return (
                        <div className="text-white-50 small text-center p-4">
                          Фотофиксация для данной стоянки отсутствует
                        </div>
                      );
                    }

                    return (
                      <>
                        <img
                          src={activePhotoUrl}
                          alt="Состояние КП"
                          style={{
                            width: "100%",
                            height: "100%",
                            maxHeight: "340px",
                            objectFit: "contain",
                            cursor: "zoom-in",
                          }}
                          title="Нажмите для увеличения на весь экран"
                          onClick={() =>
                            openLightbox(
                              editingSession.id,
                              isShowingAfter ? "Фото ПОСЛЕ" : "Фото ДО"
                            )
                          }
                        />
                        <span
                          className={`position-absolute top-0 start-0 m-2 badge ${
                            isShowingAfter ? "bg-success" : "bg-primary"
                          } shadow`}
                        >
                          {isShowingAfter ? "ПОСЛЕ ВЫГРУЗКИ" : "ДО ВЫГРУЗКИ"}
                        </span>
                        <CButton
                          size="sm"
                          color="dark"
                          className="position-absolute bottom-0 end-0 m-2 opacity-75 fw-bold"
                          onClick={() => {
                            setEditModalVisible(false);
                            onVideoJump(new Date(editingSession.start).getTime());
                          }}
                        >
                          <CIcon icon={cilMediaPlay} className="me-1" /> Смотреть видео
                        </CButton>
                      </>
                    );
                  })()}
                </div>
                <div className="text-muted small mt-1 text-center" style={{ fontSize: "0.75rem" }}>
                  💡 Нажмите на фотографию, чтобы открыть её в полном размере с зумом
                </div>
              </div>

              <div className="col-12 col-lg-6">
                <div className="mb-3">
                  <label className="form-label small fw-bold">Улица / Адрес площадки:</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingSession.address}
                    onChange={(e) => setEditingSession({ ...editingSession, address: e.target.value })}
                  />
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-bold">Баков на КП:</label>
                    <input
                      type="number"
                      min="0"
                      className="form-control fw-bold"
                      value={editingSession.containers_count}
                      onChange={(e) =>
                        setEditingSession({ ...editingSession, containers_count: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-bold">Объём навала (м³):</label>
                    <input
                      type="number"
                      step="0.1"
                      className="form-control fw-bold"
                      placeholder="0.0"
                      value={editingSession.estimated_volume_m3}
                      onChange={(e) =>
                        setEditingSession({ ...editingSession, estimated_volume_m3: e.target.value })
                      }
                    />
                  </div>
                </div>

                <label className="form-label small fw-bold mb-2">
                  Оценка состояния и нарушения после выезда:
                </label>
                <div className="d-flex flex-column gap-2 bg-light p-3 rounded border">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="swEditTko"
                      checked={editingSession.has_overflow}
                      onChange={(e) =>
                        setEditingSession({ ...editingSession, has_overflow: e.target.checked })
                      }
                    />
                    <label className="form-check-label fw-bold text-danger" htmlFor="swEditTko">
                      Навал ТКО вне контейнера
                    </label>
                  </div>

                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="swEditKgo"
                      checked={editingSession.has_bulky_waste}
                      onChange={(e) =>
                        setEditingSession({ ...editingSession, has_bulky_waste: e.target.checked })
                      }
                    />
                    <label className="form-check-label fw-bold text-warning" htmlFor="swEditKgo">
                      КГО на площадке (мебель / ветки / стройка)
                    </label>
                  </div>

                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="swEditDefect"
                      checked={editingSession.has_container_defect}
                      onChange={(e) =>
                        setEditingSession({
                          ...editingSession,
                          has_container_defect: e.target.checked,
                        })
                      }
                    />
                    <label className="form-check-label fw-bold text-secondary" htmlFor="swEditDefect">
                      Дефект контейнера
                    </label>
                  </div>

                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="swEditUnemptied"
                      checked={editingSession.has_unemptied_containers}
                      onChange={(e) =>
                        setEditingSession({
                          ...editingSession,
                          has_unemptied_containers: e.target.checked,
                        })
                      }
                    />
                    <label className="form-check-label fw-bold text-danger" htmlFor="swEditUnemptied">
                      🚨 Недогруз (остались полные баки)
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </CModalBody>
          <CModalFooter className="bg-light">
            <CButton color="secondary" onClick={() => setEditModalVisible(false)}>
              Отмена
            </CButton>
            <CButton
              color="primary"
              className="fw-bold px-4"
              onClick={handleSaveSession}
              disabled={savingEdit}
            >
              {savingEdit ? "Сохранение..." : "Сохранить изменения"}
            </CButton>
          </CModalFooter>
        </CModal>
      )}

      {/* МОДАЛЬНОЕ ОКНО ПОДТВЕРЖДЕНИЯ УДАЛЕНИЯ ЗАЕЗДА */}
      <CModal visible={deleteModalVisible} onClose={() => setDeleteModalVisible(false)} alignment="center">
        <CModalHeader className="bg-danger text-white">
          <CModalTitle className="fw-bold">Подтверждение удаления заезда</CModalTitle>
        </CModalHeader>
        <CModalBody className="p-4">
          <p className="mb-2">
            Вы уверены, что хотите удалить выбранную площадку из журнала?
          </p>
          <div className="alert alert-warning small mb-0">
            ⚠️ <b>Внимание:</b> Запись будет безвозвратно стёрта, статистика пересчитана, а отрезок стоянки <b>автоматически исчезнет с верхнего таймлайна</b>.
          </div>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setDeleteModalVisible(false)}>Отмена</CButton>
          <CButton color="danger" className="text-white fw-bold" onClick={handleConfirmDelete} disabled={deletingSession}>
            {deletingSession ? "Удаление..." : "Удалить заезд"}
          </CButton>
        </CModalFooter>
      </CModal>

      {/* МОДАЛЬНОЕ ОКНО ДЕТАЛИЗАЦИИ LLM */}
      {selectedLlmSite && selectedLlmSite.llm_analysis && (
        <CModal
          size="lg"
          visible={!!selectedLlmSite}
          onClose={() => setSelectedLlmSite(null)}
          alignment="center"
        >
          <CModalHeader className="bg-primary text-white">
            <CModalTitle className="fw-bold">
              🤖 Экспертный ИИ-анализ навалов
            </CModalTitle>
          </CModalHeader>
          <CModalBody className="p-4">
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <div className="p-3 bg-light rounded border">
                  <span className="text-muted small text-uppercase fw-bold">Категория отходов:</span>
                  <div className="fs-5 fw-bold text-dark mt-1">
                    {selectedLlmSite.llm_analysis.waste_category}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="p-3 bg-light rounded border">
                  <span className="text-muted small text-uppercase fw-bold">Ориентировочный объем:</span>
                  <div className="fs-5 fw-bold text-danger mt-1">
                    ~{selectedLlmSite.llm_analysis.estimated_volume_m3} м³
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-3 p-3 bg-light rounded border">
              <span className="text-muted small text-uppercase fw-bold">Физический ориентир масштаба:</span>
              <p className="mb-0 text-dark mt-1 fst-italic">
                "{selectedLlmSite.llm_analysis.volume_scale_anchor}"
              </p>
            </div>

            <div className="row g-3 mb-3">
              {selectedLlmSite.llm_analysis.tko_items?.length > 0 && (
                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white h-100">
                    <strong className="text-warning d-block mb-2">🗑️ Бытовой мусор (ТКО):</strong>
                    <ul className="mb-0 ps-3 small text-muted">
                      {selectedLlmSite.llm_analysis.tko_items.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {selectedLlmSite.llm_analysis.kgo_items?.length > 0 && (
                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white h-100">
                    <strong className="text-danger d-block mb-2">🛋️ Крупногабаритный мусор (КГО):</strong>
                    <ul className="mb-0 ps-3 small text-muted">
                      {selectedLlmSite.llm_analysis.kgo_items.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-3 d-flex align-items-center gap-3">
              <span className="badge bg-secondary p-2">
                Уровень риска: <strong>{selectedLlmSite.llm_analysis.hazard_level || "Не определен"}</strong>
              </span>
              {selectedLlmSite.llm_analysis.is_blocking_access && (
                <span className="badge bg-danger p-2">
                  ⚠️ Блокирует подход/подкат баков
                </span>
              )}
            </div>

            <div className="alert alert-info border-info mb-0">
              <strong className="d-block mb-1">📋 Заключение диспетчеру:</strong>
              <div className="small">
                {selectedLlmSite.llm_analysis.infraction_summary}
              </div>
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" onClick={() => setSelectedLlmSite(null)}>
              Закрыть
            </CButton>
          </CModalFooter>
        </CModal>
      )}

      {/* LIGHTBOX ПРОСМОТРА ФОТО */}
      {lightbox.isOpen && galleryItems.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.9)",
            zIndex: 99999,
            display: "flex",
            flexDirection: "column",
            userSelect: "none",
          }}
          onWheel={(e) => {
            e.preventDefault();
            setZoom((z) => Math.max(1, Math.min(z + e.deltaY * -0.002, 5)));
          }}
        >
          <div
            style={{
              padding: "15px 20px",
              background: "rgba(0,0,0,0.6)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
              zIndex: 10,
            }}
          >
            <div className="d-flex align-items-center gap-4">
              <div>
                <strong className="fs-5 text-warning">
                  {galleryItems[lightbox.currentIndex].label}
                </strong>
                <div className="text-white-50 small mt-1">
                  Время: {dayjs(galleryItems[lightbox.currentIndex].site.start).format("DD.MM.YYYY HH:mm:ss")}
                </div>
              </div>
              <div className="d-none d-md-block text-white-50 border-start border-secondary ps-4">
                <CIcon icon={cilMap} className="me-2 text-danger" />
                {galleryItems[lightbox.currentIndex].site.latitude > 0
                  ? `Координаты: ${galleryItems[lightbox.currentIndex].site.latitude.toFixed(5)}, ${galleryItems[lightbox.currentIndex].site.longitude.toFixed(5)}`
                  : "GPS недоступен"}
              </div>
              <div className="ms-3 border-start border-secondary ps-4">
                <CButton
                  color="primary"
                  size="sm"
                  className="fw-bold"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeLightbox();
                    onVideoJump(new Date(galleryItems[lightbox.currentIndex].site.start).getTime());
                  }}
                >
                  <CIcon icon={cilMediaPlay} className="me-1" /> Перейти к видео
                </CButton>
              </div>
            </div>

            <div className="d-flex gap-3 align-items-center">
              <span className="text-white-50 fw-bold me-2">
                {lightbox.currentIndex + 1} / {galleryItems.length}
              </span>
              <div className="btn-group">
                <CButton
                  color="light"
                  variant="outline"
                  size="sm"
                  title="Увеличить"
                  onClick={(e) => {
                    e.stopPropagation();
                    setZoom((z) => Math.min(z + 0.5, 5));
                  }}
                >
                  <CIcon icon={cilZoomIn} />
                </CButton>
                <CButton
                  color="light"
                  variant="outline"
                  size="sm"
                  title="Уменьшить"
                  onClick={(e) => {
                    e.stopPropagation();
                    setZoom((z) => Math.max(z - 0.5, 1));
                  }}
                >
                  <CIcon icon={cilZoomOut} />
                </CButton>
              </div>
              <CButton
                color="danger"
                variant="ghost"
                className="text-white ms-2"
                onClick={closeLightbox}
              >
                <CIcon icon={cilX} size="xl" />
              </CButton>
            </div>
          </div>

          <div
            style={{
              flexGrow: 1,
              position: "relative",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <div
              onClick={prevPhoto}
              style={{
                position: "absolute",
                left: "20px",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
                cursor: "pointer",
                background: "rgba(255,255,255,0.15)",
                borderRadius: "50%",
                padding: "15px",
                backdropFilter: "blur(4px)",
                transition: "0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
            >
              <CIcon icon={cilChevronLeft} size="xxl" className="text-white" />
            </div>

            <img
              src={galleryItems[lightbox.currentIndex].url}
              alt="Снимок КП"
              draggable={false}
              style={{
                maxWidth: "95%",
                maxHeight: "95%",
                objectFit: "contain",
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transition: isDragging ? "none" : "transform 0.15s ease",
                cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
              }}
            />

            <div
              onClick={nextPhoto}
              style={{
                position: "absolute",
                right: "20px",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
                cursor: "pointer",
                background: "rgba(255,255,255,0.15)",
                borderRadius: "50%",
                padding: "15px",
                backdropFilter: "blur(4px)",
                transition: "0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
            >
              <CIcon icon={cilChevronRight} size="xxl" className="text-white" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

GarbageTruckDashboard.propTypes = {
  data: PropTypes.object,
  onVideoJump: PropTypes.func.isRequired,
  timelineComponent: PropTypes.node,
};

export default GarbageTruckDashboard;
